import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const generateSessionId = () => {
  const k = "lm_chat_session";
  let s = localStorage.getItem(k);
  if (!s) {
    s = `lm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(k, s);
  }
  return s;
};

const greetingMessage = {
  role: "assistant",
  content:
    "Bonjour ! Je suis l'assistant de Louis-Melchior. Je peux vous renseigner sur les cours, les tarifs, la préparation mariage ou la zone de déplacement. Que puis-je faire pour vous ?",
};

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([greetingMessage]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    sessionIdRef.current = generateSessionId();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionIdRef.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Désolé, l'assistant rencontre un souci. N'hésitez pas à utiliser le formulaire de contact ci-dessous.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Anchor for nav link */}
      <span id="assistant" className="block -mt-20 pt-20" aria-hidden />

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        aria-label="Ouvrir l'assistant"
        className={`fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 w-14 h-14 rounded-full bg-[#C9A869] text-[#0E1A2B] flex items-center justify-center shadow-[0_8px_30px_rgba(201,168,105,0.45)] hover:bg-[#DCC28E] hover:scale-105 transition-all duration-500 ${
          open ? "opacity-0 pointer-events-none scale-90" : "opacity-100"
        }`}
      >
        <MessageCircle size={22} />
      </button>

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[400px] h-[560px] max-h-[80vh] bg-[#0E1A2B] border border-[#C9A869]/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col"
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C9A869]/30 bg-[#1A2A40]">
            <div>
              <p className="serif text-[#C9A869] text-lg leading-tight">Assistant</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#F7F3EA]/55">Louis-Melchior Danse</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chatbot-close"
              className="p-2 text-[#F7F3EA]/70 hover:text-[#C9A869]"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed font-light ${
                    m.role === "user"
                      ? "bg-[#C9A869] text-[#0E1A2B]"
                      : "bg-[#1A2A40] text-[#F7F3EA] border border-[#C9A869]/20"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A2A40] border border-[#C9A869]/20 px-4 py-3 text-sm text-[#F7F3EA]/60">
                  <span className="inline-block w-2 h-2 bg-[#C9A869] rounded-full animate-pulse mr-1" />
                  <span className="inline-block w-2 h-2 bg-[#C9A869] rounded-full animate-pulse mr-1" style={{ animationDelay: "150ms" }} />
                  <span className="inline-block w-2 h-2 bg-[#C9A869] rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#C9A869]/30 bg-[#0E1A2B] flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Posez votre question…"
              data-testid="chatbot-input"
              className="flex-1 bg-transparent border border-[#C9A869]/30 text-[#F7F3EA] px-3 py-2 text-sm focus:border-[#C9A869] outline-none"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="chatbot-send"
              className="px-4 bg-[#C9A869] text-[#0E1A2B] hover:bg-[#DCC28E] disabled:opacity-50 transition-colors"
              aria-label="Envoyer"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
